import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Lock, Check, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalcAmountInput } from "@/components/CalcAmountInput";
import { DatePickerCeKKeU } from "@/components/DatePickerCeKKeU";
import { useFinance } from "@/context/FinanceContext";
import { QUICK_CATEGORIES } from "@/lib/finance";

export default function QuickEntry() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { addTransaction, updateTransaction, init, hasHousehold, finance } = useFinance();

  const editId = params.get("edit");
  const editing = (finance?.transactions || []).find((t) => t.id === editId) || null;

  const today = new Date().toISOString().slice(0, 10);
  const minDate = new Date(Date.now() - 31 * 86400000).toISOString().slice(0, 10);

  const [type, setType] = useState(
    editing ? editing.type : params.get("type") === "income" ? "income" : "expense"
  );
  const [amount, setAmount] = useState(editing ? editing.amount : 0);
  const [amountExpr, setAmountExpr] = useState(editing ? editing.amountExpr || null : null);
  const [cat, setCat] = useState(
    editing && editing.type === "expense"
      ? QUICK_CATEGORIES.find((c) => c.label === editing.category)?.id || null
      : null
  );
  const [note, setNote] = useState(editing ? editing.note || "" : "");
  const [date, setDate] = useState(editing ? String(editing.date).slice(0, 10) : today);

  // One-time onboarding tip (only for brand-new loggers, not when editing).
  const TIP_KEY = "fcs_catat_tip_seen";
  const [showTip, setShowTip] = useState(() => {
    if (editing) return false;
    try { return localStorage.getItem(TIP_KEY) !== "1"; } catch { return false; }
  });
  const dismissTip = () => {
    try { localStorage.setItem(TIP_KEY, "1"); } catch { /* ignore */ }
    setShowTip(false);
  };

  const save = () => {
    if (!amount || amount <= 0) return toast.error("Masukkan jumlah");
    if (type === "expense" && !cat) return toast.error("Pilih kategori");
    if (date < minDate || date > today) return toast.error("Tanggal maksimal 31 hari lalu, tidak boleh masa depan");
    if (!hasHousehold) init();
    const c = QUICK_CATEGORIES.find((x) => x.id === cat);
    const payload = {
      type,
      amount,
      amountExpr: amountExpr || null,
      category: type === "expense" ? c?.label : "Pemasukan",
      classification: type === "expense" ? c?.classification : null,
      date,
      note,
    };
    if (editing) {
      updateTransaction(editing.id, payload);
      toast.success("Transaksi diperbarui");
    } else {
      addTransaction(payload);
      toast.success(type === "income" ? "Pemasukan tercatat" : "Pengeluaran tercatat");
    }
    navigate("/app/catatan");
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-2xl bg-white px-6 pt-6" data-testid="quick-entry-page">
      <header className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold text-zinc-900">{editing ? "Edit Transaksi" : "Catat Transaksi"}</h1>
        <button data-testid="quick-close" onClick={() => navigate(-1)} className="text-zinc-400">
          <X className="h-6 w-6" />
        </button>
      </header>

      {/* One-time onboarding tip */}
      {showTip && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="catat-onboarding-tip">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
              <Lightbulb className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-bold text-emerald-900">Cara mencatat (10 detik)</p>
              <ol className="mt-1.5 list-decimal space-y-1 pl-4 text-xs leading-relaxed text-emerald-800">
                <li>Pilih <b>Pengeluaran</b> atau <b>Pemasukan</b>.</li>
                <li>Ketik <b>jumlah</b> — bisa hitung langsung, mis. 125000 + 75000.</li>
                <li>Pilih <b>kategori</b> (untuk pengeluaran) & atur <b>tanggal</b> bila perlu.</li>
                <li>Tekan <b>Simpan</b>. Selesai!</li>
              </ol>
            </div>
            <button
              data-testid="catat-tip-dismiss"
              onClick={dismissTip}
              className="shrink-0 text-emerald-400 transition-colors hover:text-emerald-600"
              aria-label="Tutup tip"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <button
            data-testid="catat-tip-ok"
            onClick={dismissTip}
            className="mt-3 w-full rounded-full bg-emerald-600 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Mengerti
          </button>
        </div>
      )}

      {/* Type toggle */}
      <div className="mb-6 flex gap-2 rounded-full bg-zinc-100 p-1">        {[
          { id: "expense", label: "Pengeluaran" },
          { id: "income", label: "Pemasukan" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`type-${t.id}`}
            onClick={() => setType(t.id)}
            className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${
              type === t.id ? (t.id === "income" ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white") : "text-zinc-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Amount */}
      <CalcAmountInput
        label="Jumlah"
        testId="quick-amount"
        value={amount}
        onChange={setAmount}
        onExpr={setAmountExpr}
        initialExpr={editing ? editing.amountExpr || undefined : undefined}
      />

      {/* Categories (expense only) */}
      {type === "expense" && (
        <>
          <p className="mb-2 mt-6 text-sm font-medium text-zinc-700 font-heading">Kategori</p>
          <div className="grid grid-cols-3 gap-2" data-testid="quick-categories">
            {QUICK_CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`cat-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`rounded-2xl border py-3 text-xs font-semibold transition-colors ${
                  cat === c.id ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Note (optional) */}
      <label className="mb-1.5 mt-6 block text-sm font-medium text-zinc-700 font-heading">Catatan (opsional)</label>
      <Input
        data-testid="quick-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={type === "income" ? "mis. Gaji bulanan, bonus, hasil usaha" : "mis. Belanja dapur, bensin, makan siang"}
        className="h-12 rounded-2xl border-zinc-200"
      />
      {/* Transaction date */}
      <label className="mb-1.5 mt-6 block text-sm font-medium text-zinc-700 font-heading">Tanggal transaksi</label>
      <DatePickerCeKKeU
        testId="quick-date"
        value={date}
        min={minDate}
        max={today}
        onChange={setDate}
      />
      <p className="mt-1.5 text-xs text-zinc-400">Bisa mundur hingga 31 hari. Tidak bisa memilih tanggal yang akan datang.</p>

      <Button
        data-testid="quick-save"
        onClick={save}
        className="mt-6 h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
      >
        <Check className="mr-2 h-5 w-5" /> Simpan
      </Button>

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-emerald-50 p-3">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <p className="text-xs leading-relaxed text-emerald-700">
          Angka keuangan yang Anda masukkan tetap pribadi dan tidak dapat dilihat oleh admin CeKKeU.
        </p>
      </div>
    </div>
  );
}
